import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Modal from './Modal';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Component to handle map clicks
function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng);
    },
  });
  return null;
}

function LocationsPage() {
  const [locations, setLocations] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationName, setLocationName] = useState('');
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [mapCenter, setMapCenter] = useState([39.5, -8.0]); // Default to Portugal center
  const [mapZoom, setMapZoom] = useState(7); // Zoom level to show Portugal
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    // Update map center if we have locations with coordinates
    const locationsWithCoords = locations.filter(loc => loc.latitude && loc.longitude);
    if (locationsWithCoords.length > 0) {
      const avgLat = locationsWithCoords.reduce((sum, loc) => sum + loc.latitude, 0) / locationsWithCoords.length;
      const avgLng = locationsWithCoords.reduce((sum, loc) => sum + loc.longitude, 0) / locationsWithCoords.length;
      setMapCenter([avgLat, avgLng]);
    } else {
      // Try to get user's location
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setMapCenter([position.coords.latitude, position.coords.longitude]);
            setMapZoom(13);
          },
          () => {
            // If geolocation fails, keep default
          }
        );
      }
    }
  }, [locations]);

  useEffect(() => {
    if (editingLocation) {
      // Ensure we get the name as a string
      const name = typeof editingLocation.name === 'string' 
        ? editingLocation.name 
        : (editingLocation.name?.name || '');
      setLocationName(name);
      if (editingLocation.latitude && editingLocation.longitude) {
        setSelectedPosition({
          lat: editingLocation.latitude,
          lng: editingLocation.longitude
        });
        setMapCenter([editingLocation.latitude, editingLocation.longitude]);
      } else {
        setSelectedPosition(null);
      }
      setShowAddForm(true);
    }
  }, [editingLocation]);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API_BASE}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const handleMapClick = (latlng) => {
    setSelectedPosition(latlng);
    // Reverse geocode to get address (optional - for now just use coordinates)
    if (!locationName) {
      setLocationName(`${latlng.lat.toFixed(6)}, ${latlng.lng.toFixed(6)}`);
    }
  };

  const handleAddLocation = async (e) => {
    e.preventDefault();
    
    if (!locationName.trim()) {
      alert('Please enter a location name');
      return;
    }

    try {
      if (editingLocation) {
        await axios.put(`${API_BASE}/locations/${editingLocation.id}`, {
          name: locationName.trim(),
          latitude: selectedPosition ? selectedPosition.lat : null,
          longitude: selectedPosition ? selectedPosition.lng : null
        });
      } else {
        await axios.post(`${API_BASE}/locations`, {
          name: locationName.trim(),
          latitude: selectedPosition ? selectedPosition.lat : null,
          longitude: selectedPosition ? selectedPosition.lng : null
        });
      }
      await fetchLocations();
      setLocationName('');
      setSelectedPosition(null);
      setShowAddForm(false);
      setEditingLocation(null);
    } catch (error) {
      console.error('Error saving location:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      alert(`Error saving location: ${errorMessage}`);
    }
  };

  const handleEditLocation = (location) => {
    setEditingLocation(location);
  };

  const handleCancelForm = () => {
    setShowAddForm(false);
    setEditingLocation(null);
    setLocationName('');
    setSelectedPosition(null);
  };

  const handleDeleteLocation = async (id) => {
    if (window.confirm('Are you sure you want to delete this location? This will not remove it from existing gigs, but it will be removed from the location list.')) {
      try {
        await axios.delete(`${API_BASE}/locations/${id}`);
        await fetchLocations();
      } catch (error) {
        console.error('Error deleting location:', error);
        alert('Error deleting location. Please try again.');
      }
    }
  };

  // Filter locations based on search query
  const filteredLocations = locations.filter(location => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const locationName = typeof location.name === 'string' ? location.name : (location.name?.name || '');
    return locationName.toLowerCase().includes(query);
  });

  return (
    <div className="locations-page">
      <div className="locations-header">
        <div>
          <h1>Locations</h1>
          <p className="locations-subtitle">All locations used in your gigs</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => {
            if (showAddForm) {
              handleCancelForm();
            } else {
              setEditingLocation(null);
              setLocationName('');
              setSelectedPosition(null);
              setShowAddForm(true);
            }
          }}
        >
          {showAddForm ? 'Cancel' : '+ Add Location'}
        </button>
      </div>

      <div className="search-bar-container">
        <input
          type="text"
          placeholder="Search locations by name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
      </div>

      <Modal
        isOpen={showAddForm}
        onClose={handleCancelForm}
        title={editingLocation ? 'Edit Location' : 'Add New Location'}
      >
        <form onSubmit={handleAddLocation}>
            <div className="form-group">
              <label htmlFor="location-name">Location Name *</label>
              <input
                type="text"
                id="location-name"
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
                required
                placeholder="e.g., Office Building, Conference Center, etc."
              />
            </div>
            
            <div className="form-group">
              <label>Click on the map to select a location (optional)</label>
              <div className="map-container-small">
                <MapContainer
                  center={editingLocation && editingLocation.latitude && editingLocation.longitude 
                    ? [editingLocation.latitude, editingLocation.longitude]
                    : selectedPosition 
                    ? [selectedPosition.lat, selectedPosition.lng]
                    : mapCenter}
                  zoom={mapZoom}
                  style={{ height: '300px', width: '100%', borderRadius: '8px' }}
                  key={editingLocation ? editingLocation.id : 'new'}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapClickHandler onMapClick={handleMapClick} />
                  {(selectedPosition || (editingLocation && editingLocation.latitude && editingLocation.longitude)) && (
                    <Marker 
                      position={
                        selectedPosition 
                          ? [selectedPosition.lat, selectedPosition.lng]
                          : [editingLocation.latitude, editingLocation.longitude]
                      } 
                    />
                  )}
                </MapContainer>
              </div>
              {(selectedPosition || (editingLocation && editingLocation.latitude && editingLocation.longitude)) && (
                <p style={{ color: '#999', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                  {selectedPosition 
                    ? `Selected: ${selectedPosition.lat.toFixed(6)}, ${selectedPosition.lng.toFixed(6)}`
                    : `Coordinates: ${editingLocation.latitude.toFixed(6)}, ${editingLocation.longitude.toFixed(6)}`}
                </p>
              )}
            </div>

            <div className="btn-group">
              <button type="submit" className="btn btn-primary">
                {editingLocation ? 'Update Location' : 'Add Location'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancelForm}
              >
                Cancel
              </button>
            </div>
          </form>
      </Modal>

      <div className="locations-content">
        {filteredLocations.length > 0 && (
          <div className="locations-map-view">
            <h2>Locations Map</h2>
            <div className="map-container-large">
              <MapContainer
                center={mapCenter}
                zoom={filteredLocations.some(loc => loc.latitude && loc.longitude) ? 10 : mapZoom}
                style={{ height: '500px', width: '100%', borderRadius: '8px' }}
                key={filteredLocations.length} // Force re-render when locations change
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {filteredLocations
                  .filter(loc => loc.latitude && loc.longitude)
                  .map(location => {
                    const locationName = typeof location.name === 'string' 
                      ? location.name 
                      : (location.name?.name || 'Unknown Location');
                    return (
                      <Marker
                        key={location.id}
                        position={[location.latitude, location.longitude]}
                      >
                        <Popup>
                          <strong>{locationName}</strong>
                        </Popup>
                      </Marker>
                    );
                  })}
              </MapContainer>
            </div>
          </div>
        )}

        <div className="locations-list">
          <h2>All Locations</h2>
          {filteredLocations.length > 0 ? (
            <div className="locations-grid">
              {filteredLocations.map(location => {
                // Ensure location.name is a string
                const locationName = typeof location.name === 'string' ? location.name : (location.name?.name || 'Unknown Location');
                return (
                  <div key={location.id} className="location-card">
                    <div className="location-card-header">
                      <h3>📍 {locationName}</h3>
                      <div className="location-actions">
                        <button
                          className="btn-icon"
                          onClick={() => handleEditLocation(location)}
                          title="Edit location"
                        >
                          Edit
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => handleDeleteLocation(location.id)}
                          title="Delete location"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <div className="location-card-body">
                      <div className="location-info">
                        <strong>Added:</strong> {location.created_at ? new Date(location.created_at).toLocaleDateString() : 'Unknown'}
                      </div>
                      {location.latitude && location.longitude && (
                        <div className="location-info">
                          <strong>Coordinates:</strong> {typeof location.latitude === 'number' ? location.latitude.toFixed(6) : location.latitude}, {typeof location.longitude === 'number' ? location.longitude.toFixed(6) : location.longitude}
                        </div>
                      )}
                      {(!location.latitude || !location.longitude) && (
                        <div className="location-info" style={{ color: '#999', fontStyle: 'italic' }}>
                          No map coordinates set
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : locations.length > 0 ? (
            <div className="empty-state">
              <p>No locations match your search query.</p>
            </div>
          ) : (
            <div className="empty-state">
              <p>No locations yet. Locations will be added automatically when you enter a venue/location in a gig.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LocationsPage;

